/**
 * Which campuses the SCHOOL ORDER surfaces offer.
 *
 * `liveAt` marks a campus PROVISIONED, and that happens long before anyone
 * authors its menus — so a site that "lights up later" sat in the campus
 * selector handing the admin an order sheet with nothing on it. `my-schools`
 * now reports `hasOrderableMenu` per campus and this is the one place that
 * acts on it, so the week view's selector and the checkout's campus tabs can
 * never disagree about which campuses exist.
 *
 * Only the ORDERING surfaces filter. Campus setup — grade ranges, roster
 * import, device pairing, staff invitations, auto-orders, district split — all
 * read the same payload and must keep showing every campus, because those are
 * exactly what you use to stand one up before its menus exist.
 */

import type { AdminSchool } from '@/api/school-orders'

/**
 * `hasOrderableMenu` filtered, with two deliberate fail-open rules:
 *
 *  - `undefined` counts as orderable. A payload cached before this field
 *    shipped must not blank the selector for the seconds until it refetches.
 *  - If NO campus qualifies, every campus is returned. An admin whose schools
 *    are all still being provisioned should meet the ordering surface's own
 *    "nothing on the menu yet" state, not an empty switcher that reads as
 *    "you have no schools" — which is a different problem with a different fix.
 */
export function orderableCampuses(schools: readonly AdminSchool[]): AdminSchool[] {
  const orderable = schools.filter((s) => s.hasOrderableMenu !== false)
  return orderable.length > 0 ? orderable : [...schools]
}

/** True when `schools` holds a campus the ordering surfaces are hiding. */
export function hasHiddenCampuses(schools: readonly AdminSchool[]): boolean {
  const orderable = orderableCampuses(schools)
  return orderable.length < schools.length
}

/**
 * Does `school` accept SCHOOL-placed orders? False only for a PARENT-only
 * campus — the server's `schoolOrderingEnabled`, computed by the submit gate's
 * own predicate (server `schoolOrderingAllowed`), so the client never offers
 * an order the submit path refuses with `school_ordering_not_enabled`.
 *
 * Unlike `hasOrderableMenu`, this never drops a campus from a list: a
 * PARENT-only campus is the admin's school, so the ordering surfaces show it
 * READ-ONLY (its parent demand still renders). Callers fold this into their
 * `readOnly`.
 *
 * `undefined` reads as true: a payload cached before the field shipped never
 * listed a PARENT-only campus in the first place.
 */
export function acceptsSchoolOrders(school: Pick<AdminSchool, 'schoolOrderingEnabled'>): boolean {
  return school.schoolOrderingEnabled !== false
}
